import React, { useState, useRef } from 'react';
import type { Checklist } from '../types/checklist.types';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import documentsService, { linkEvidenceToSource } from '@/services/documentsService';
import { DatePicker } from '@/components/ui/date-picker';

interface ChecklistCompleteFormProps {
  checklist: Checklist;
  onSubmit?: (values: Record<string, any>) => void;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
  onProgressChange?: (progress: {
    totalItems: number;
    evidenceRequiredCount: number;
    completedCount: number;
    requiredSatisfied: number;
    requiredTotal: number;
  }) => void;
}

export default function ChecklistCompleteForm({ checklist, onSubmit, submitting, setSubmitting, onProgressChange }: ChecklistCompleteFormProps) {
  const [values, setValues] = useState<Record<string, any>>(() => {
    // Initialize state for each item
    const initial: Record<string, any> = {};
    checklist.items.forEach(item => {
      initial[item.id] = '';
    });
    return initial;
  });

  const [error, setError] = useState<string | null>(null);
  const [evidenceUploading, setEvidenceUploading] = useState<Record<string, boolean>>({});
  const [dragging, setDragging] = useState<Record<string, boolean>>({});
  const [evidenceDisplayNames, setEvidenceDisplayNames] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const frostedFieldClasses = 'bg-background border border-border/60 focus-visible:ring-primary/30 focus-visible:ring-offset-0 placeholder:text-muted-foreground';
  const totalItems = checklist.items.length;
  const evidenceRequiredCount = checklist.items.filter(item => item.requireEvidence).length;
  const completedCount = checklist.items.reduce((count, item) => {
    const value = values[item.id];
    const evidenceValue = values[`evidence-${item.id}`];
    const satisfiesEvidence = !item.requireEvidence || Boolean(evidenceValue);
    if (item.fieldType === 'Checkbox') {
      return count + (value && satisfiesEvidence ? 1 : 0);
    }
    return count + (value !== undefined && value !== '' && satisfiesEvidence ? 1 : 0);
  }, 0);

  const requiredItems = checklist.items.filter(item => item.requireEvidence);
  const requiredTotal = requiredItems.length;
  const requiredSatisfied = requiredItems.filter(item => {
    const value = values[item.id];
    const hasEvidence = Boolean(values[`evidence-${item.id}`]);
    if (item.fieldType === 'Checkbox') {
      return Boolean(value) && hasEvidence;
    }
    return value !== undefined && value !== '' && hasEvidence;
  }).length;

  React.useEffect(() => {
    onProgressChange?.({ totalItems, evidenceRequiredCount, completedCount, requiredSatisfied, requiredTotal });
  }, [onProgressChange, totalItems, evidenceRequiredCount, completedCount, requiredSatisfied, requiredTotal]);

  const handleChange = (id: string, value: any) => {
    setValues(v => ({ ...v, [id]: value }));
  };

  const handleDragState = (itemId: string, isDragging: boolean) => {
    setDragging(prev => ({ ...prev, [itemId]: isDragging }));
  };

  const handleEvidenceClear = (itemId: string) => {
    void handleEvidenceUpload(itemId, null);
    const input = fileInputRefs.current[itemId];
    if (input) {
      input.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (onSubmit) await onSubmit(values);
    } catch (err: any) {
      setError(err.message || 'Failed to submit checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEvidenceUpload = async (itemId: string, file: File | undefined | null) => {
    if (!file) {
      handleChange(`evidence-${itemId}`, '');
      setEvidenceDisplayNames(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      return;
    }
    try {
      setEvidenceUploading(prev => ({ ...prev, [itemId]: true }));
      // Prepare minimal document template for Evidence
      // DED-1472: Add source checklist information for MCM linking
      const documentTemplate: any = {
        fileName: file.name,
        documentType: 'Evidence',
        classification: 'Business Confidential',
        status: 'Current',
        comments: `Checklist evidence for item ${itemId} in "${checklist.title}"`,
        sourceChecklistId: checklist.id,
        sourceChecklistTitle: checklist.title,
        sourceChecklistItemId: itemId,
      };
      const created = await documentsService.createDocument(documentTemplate, file, null);
      
      // DED-1472: Link evidence back to the source checklist using MCM
      if (created?.docID && checklist.id) {
        await linkEvidenceToSource(
          created.componentId || `SUP-${created.docID}`,
          {
            sourceType: 'checklist',
            sourceId: checklist.id,
            sourceItemId: itemId,
            sourceTitle: checklist.title,
          },
          null // Use default companyID
        );
      }
      
      // Store created docID as the value; UI can resolve later if needed
      handleChange(`evidence-${itemId}`, created?.docID || file.name);
      setEvidenceDisplayNames(prev => ({ ...prev, [itemId]: file.name }));
    } catch (e: any) {
      setError(e?.message || 'Failed to upload evidence');
      // Fallback to filename so user sees something
      handleChange(`evidence-${itemId}`, file?.name || '');
      setEvidenceDisplayNames(prev => ({ ...prev, [itemId]: file.name }));
    } finally {
      setEvidenceUploading(prev => ({ ...prev, [itemId]: false }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checklist-complete-form space-y-6">
      <div className="space-y-5">
        {checklist.items.map((item, index) => (
          <div
            key={item.id}
            className="rounded-xl border border-border/60 bg-muted/20 p-5 shadow-sm shadow-primary/10 backdrop-blur"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  {index + 1}. {item.title}
                  {item.requireEvidence && <span className="ml-1 text-destructive">*</span>}
                </p>
                {item.notes && <p className="text-xs text-muted-foreground">{item.notes}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {/* <div className="rounded-full border border-border/50 bg-card/70 px-3 py-1 text-xs font-medium text-muted-foreground">
                  {item.fieldType}
                </div>
                {item.requireEvidence && (
                  <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                    Evidence required
                  </Badge>
                )} */}
              </div>
            </div>
            <div className="mt-4">
              {item.fieldType === 'Checkbox' ? (
                <div className="flex items-center gap-3 rounded-lg border border-border/40 bg-card/80 px-4 py-3">
                  <Checkbox
                    checked={Boolean(values[item.id])}
                    onCheckedChange={checked => handleChange(item.id, checked === true)}
                    disabled={submitting}
                    className="size-5 data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                    aria-label={`Mark ${item.title} as complete`}
                  />
                  <span className="text-sm text-muted-foreground">Mark as complete</span>
                </div>
              ) : item.fieldType === 'Number' ? (
                <Input
                  type="number"
                  value={values[item.id]}
                  onChange={e => handleChange(item.id, e.target.value)}
                  disabled={submitting}
                  className={`${frostedFieldClasses} h-12 w-full rounded-lg`}
                />
              ) : item.fieldType === 'Date' ? (
                // <Input
                //   type="date"
                //   value={values[item.id]}
                //   onChange={e => handleChange(item.id, e.target.value)}
                //   disabled={submitting}
                //   className={`${frostedFieldClasses} h-12 w-full rounded-lg`}
                // />
                <DatePicker
                  date={values[item.id] || new Date()}
                  setDate={date => handleChange(item.id, date)}
                  readOnly={submitting}
                  showPreview={false}
                  className={`${frostedFieldClasses} h-12 w-full rounded-lg`}
                />
              ) : item.fieldType === 'Signature' ? (
                <div className="flex min-h-[72px] items-center justify-between gap-4 rounded-lg border border-dashed border-border/60 bg-card/80 px-4 py-3 text-sm text-muted-foreground">
                  {/* TODO: Integrate signature pad component */}
                  <span>Signature input coming soon</span>
                </div>
              ) : (
                <Input
                  type="text"
                  value={values[item.id]}
                  onChange={e => handleChange(item.id, e.target.value)}
                  disabled={submitting}
                  className={`${frostedFieldClasses} h-12 w-full rounded-lg`}
                />
              )}
            </div>
            {item.requireEvidence && (
              <div
                className={`mt-4 rounded-xl border border-dashed ${dragging[item.id] ? 'border-primary bg-primary/15 hover:bg-primary/20' : values[`evidence-${item.id}`] ? 'border-primary/60 bg-primary/10' : 'border-primary/30 bg-primary/5 hover:bg-primary/15'} p-4 transition-colors`}
              >
                {values[`evidence-${item.id}`] ? (
                  <div className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-white/80 p-4 shadow-sm">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/70">Evidence attached</span>
                      <span className="text-sm font-medium text-foreground">
                        {evidenceDisplayNames[item.id] || String(values[`evidence-${item.id}`])}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRefs.current[item.id]?.click()}
                        disabled={submitting}
                      >
                        Replace evidence
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEvidenceClear(item.id)}
                        disabled={submitting}
                      >
                        Remove
                      </Button>
                      {evidenceUploading[item.id] && (
                        <Badge variant="secondary" className="border-primary/30 bg-primary/10 text-primary">
                          Uploading…
                        </Badge>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex cursor-pointer flex-col gap-2 rounded-lg border border-none bg-transparent px-2 py-1 text-primary shadow-none focus:outline-none"
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRefs.current[item.id]?.click()}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        fileInputRefs.current[item.id]?.click();
                      }
                    }}
                    onDragOver={e => {
                      e.preventDefault();
                      handleDragState(item.id, true);
                    }}
                    onDragLeave={e => {
                      e.preventDefault();
                      handleDragState(item.id, false);
                    }}
                    onDrop={e => {
                      e.preventDefault();
                      handleDragState(item.id, false);
                      const droppedFile = e.dataTransfer.files?.[0];
                      if (droppedFile) {
                        void handleEvidenceUpload(item.id, droppedFile);
                      }
                    }}
                  >
                    <span className="text-sm font-semibold">Attach supporting evidence</span>
                    <span className="text-xs text-primary/80">
                      Drag & drop a file here or click to browse
                    </span>
                  </div>
                )}
                <input
                  type="file"
                  className="hidden"
                  ref={el => {
                    fileInputRefs.current[item.id] = el;
                  }}
                  onChange={e => handleEvidenceUpload(item.id, e.target.files?.[0] || null)}
                  disabled={submitting || !!evidenceUploading[item.id]}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-center text-sm font-medium text-destructive">
          {error}
        </div>
      )}
    </form>
  );
}
